// Vault 主页: 瀑布流 + 筛选/分组/选择模式/灯箱/详情/上传/拖拽
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import { cacheFiles, getCachedFiles, cacheThumb, type FileItem } from '../lib/db';
import { makeT, type Lang } from '../lib/i18n';
import { useStore, type Filter } from '../lib/store';
import { isImage, isVideo, monthKey, formatBytes, formatDate, getThumbBlob, videoDuration, getMediaMeta, generateThumb, uploadThumb } from '../lib/utils';
import { uploadManager } from '../lib/uploadManager';
import { VaultLightbox } from '../components/Lightbox';
import { DetailPanel } from '../components/DetailPanel';
import { UploadPanel } from '../components/UploadPanel';
import { EmptyState } from '../components/EmptyState';
import { TopMenu } from '../components/TopMenu';
import { ConfirmModal } from '../components/ConfirmModal';

export function Vault({ onLogout, onShowTrash }: { onLogout: () => void; onShowTrash: () => void }) {
  const { theme, lang, filter, inSelectMode, selected, toggleTheme, setLang, setFilter, enterSelect, toggleSelect, exitSelect, selectAll } = useStore();
  const t = makeT(lang);

  const [items, setItems] = useState<FileItem[]>([]);
  const [quota, setQuota] = useState<{ usedBytes: number; fileCount: number; limitBytes: number } | null>(null);
  const [lightbox, setLightbox] = useState<{ open: boolean; index: number }>({ open: false, index: 0 });
  const [detail, setDetail] = useState<FileItem | null>(null);
  const [toast, setToast] = useState('');
  const [delConfirm, setDelConfirm] = useState<FileItem[] | null>(null);
  const [dragging, setDragging] = useState(false);
  const [hideTop, setHideTop] = useState(false);
  const mainRef = useRef<HTMLElement>(null);
  const lastScrollY = useRef(0);
  const scrollTimer = useRef<number | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);

  // ---- 加载列表: stale-while-revalidate ----
  const load = useCallback(async (useCacheFirst = true) => {
    try {
      if (useCacheFirst) {
        const cached = await getCachedFiles();
        if (cached.length > 0) setItems(cached);
      }
      const [list, q] = await Promise.all([api.get('/api/files'), api.get('/api/quota')]);
      const newItems: FileItem[] = list.items;
      setItems(newItems);
      setQuota(q);
      cacheFiles(newItems); // 写缓存
    } catch {
      // 网络失败: 保持缓存
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // 顶部自动隐藏: 向下滚动藏, 向上滚/停1.5s显示
  useEffect(() => {
    const main = mainRef.current;
    if (!main) return;
    const onScroll = () => {
      const y = main.scrollTop;
      if (y < 10) { setHideTop(false); lastScrollY.current = y; return; }
      if (y > lastScrollY.current + 6) {
        setHideTop(true);
      } else if (y < lastScrollY.current - 6) {
        setHideTop(false);
      }
      lastScrollY.current = y;
      if (scrollTimer.current) clearTimeout(scrollTimer.current);
      scrollTimer.current = window.setTimeout(() => setHideTop(false), 1500);
    };
    main.addEventListener('scroll', onScroll);
    return () => { main.removeEventListener('scroll', onScroll); if (scrollTimer.current) clearTimeout(scrollTimer.current); };
  }, []);

  // ---- 缩略图: 缓存 + 懒加载 ----
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  // 简单: 只显示后端缩略图(有则用), 不做逐个生成(避免刷屏/413)
  useEffect(() => {
    const thumbUris: Record<string, string> = {};
    for (const it of items) {
      thumbUris[it.uuid] = '/api/thumbs/' + it.uuid;
    }
    setThumbs(thumbUris);
  }, [items]);

  const showToast = (m: string) => { setToast(m); setTimeout(() => setToast(''), 2500); };

  // ---- 过滤 + 排序 + 分组 ----
  const filtered = useMemo(() => {
    let arr = items;
    if (filter === 'image') arr = arr.filter((i) => isImage(i.mime));
    if (filter === 'video') arr = arr.filter((i) => isVideo(i.mime));
    return [...arr].sort((a, b) => (b.takenAt ?? b.uploadedAt) - (a.takenAt ?? a.uploadedAt));
  }, [items, filter]);

  // 首页各类数量统计
  const counts = useMemo(() => {
    let img = 0, vid = 0;
    for (const i of items) {
      if (isImage(i.mime)) img++;
      else if (isVideo(i.mime)) vid++;
    }
    return { all: items.length, image: img, video: vid };
  }, [items]);

  const groups = useMemo(() => {
    const map = new Map<string, FileItem[]>();
    for (const it of filtered) {
      const k = monthKey(it.takenAt ?? it.uploadedAt);
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(it);
    }
    return Array.from(map.entries());
  }, [filtered]);

  // ---- 上传入口 ----
  const onPick = useCallback((files: FileList | null) => {
    if (!files) return;
    for (const f of Array.from(files)) {
      // 提取媒体宽高/时长 + 生成缩略图, 上传时带上
      getMediaMeta(f).then(async (meta) => {
        const id = await uploadManager.add(f, meta);
        // 上传后生成缩略图存后端
        try {
          const thumb = await generateThumb(f, f.type);
          await uploadThumb(id, thumb);
        } catch {}
      }).catch(() => uploadManager.add(f, {}));
    }
    load();
  }, [load]);

  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => { onPick(e.target.files); if (fileRef.current) fileRef.current.value = ''; };

  // ---- 拖拽上传(整页) ----
  useEffect(() => {
    const enter = (e: DragEvent) => { e.preventDefault(); dragDepth.current++; setDragging(true); };
    const over = (e: DragEvent) => { e.preventDefault(); };
    const leave = (e: DragEvent) => { e.preventDefault(); dragDepth.current--; if (dragDepth.current <= 0) setDragging(false); };
    const drop = (e: DragEvent) => {
      e.preventDefault(); dragDepth.current = 0; setDragging(false);
      if (e.dataTransfer?.files?.length) onPick(e.dataTransfer.files);
    };
    window.addEventListener('dragenter', enter);
    window.addEventListener('dragover', over);
    window.addEventListener('dragleave', leave);
    window.addEventListener('drop', drop);
    return () => { window.removeEventListener('dragenter', enter); window.removeEventListener('dragover', over); window.removeEventListener('dragleave', leave); window.removeEventListener('drop', drop); };
  }, [onPick]);

  // ---- 删除 ----
  const doDelete = async (uuidList: string[], hard = false) => {
    try {
      await api.del('/api/files' + (hard ? '?hard=true' : ''), { keys: uuidList });
      showToast(t('confirm.move'));
      setDelConfirm(null);
      setLightbox((l) => ({ open: false, index: 0 }));
      setDetail(null);
      await load();
    } catch (e: any) { showToast(t('upload.failed') + ' ' + e.message); }
  };

  const onTileClick = (it: FileItem) => {
    if (longPressFired.current) { longPressFired.current = false; return; }
    if (inSelectMode) { toggleSelect(it.uuid); return; }
    const idx = filtered.findIndex((x) => x.uuid === it.uuid);
    setLightbox({ open: true, index: idx });
  };
  // 长按进入选择(鼠标+触摸)
  const pressTimer = useRef<number | null>(null);
  const longPressFired = useRef(false);
  const onPressStart = (it: FileItem) => {
    if (inSelectMode) return;
    longPressFired.current = false;
    pressTimer.current = window.setTimeout(() => { longPressFired.current = true; enterSelect(it.uuid); }, 600);
  };
  const onPressEnd = () => {
    if (pressTimer.current) { clearTimeout(pressTimer.current); pressTimer.current = null; }
  };


  const selectedItems = items.filter((i) => selected.has(i.uuid));

  return (
    <div className="app">
      {/* 顶部栏 */}
      <header className={'header' + (hideTop ? ' hide-top' : '')}>
        <h1>📷 {t('appTitle')}</h1>
        <div className="actions">
          <button className="icon-btn" onClick={() => load()} title={t('header.refresh')}>↻</button>
          <TopMenu
            lang={lang}
            theme={theme}
            items={items}
            quota={quota}
            onToggleLang={() => setLang(lang === 'zh' ? 'en' : 'zh')}
            onToggleTheme={toggleTheme}
            onShowTrash={onShowTrash}
            onLogout={onLogout}
          />
        </div>
      </header>

      {/* 筛选 */}
      <div className={'tabs' + (hideTop ? ' hide-top' : '')}>
        {(['all', 'image', 'video'] as Filter[]).map((f) => (
          <button key={f} className={'tab' + (filter === f ? ' active' : '')} onClick={() => setFilter(f)}>
            {t('tab.' + f)}
            <span className="tab-count">{counts[f as keyof typeof counts]}</span>
          </button>
        ))}
      </div>

      {/* 选择模式顶栏 */}
      {inSelectMode && (
        <div className="select-bar">
          <span className="count">{selected.size}</span>
          <span className="sp" />
          <button onClick={() => selectAll(filtered.map((i) => i.uuid))}>{t('ops.selectAll')}</button>
          <button onClick={() => { setDelConfirm(selectedItems); }} className="danger">{t('bulk.delete')}</button>
          <button onClick={exitSelect}>{t('header.cancel')}</button>
        </div>
      )}

      <main className="main" ref={mainRef}>
        {items.length === 0 ? (
          <EmptyState lang={lang} onPick={() => fileRef.current?.click()} onCapture={() => fileRef.current?.click()} />
        ) : (
          groups.map(([mk, gitems]) => (
            <div key={mk}>
              <div className="month-head">{mk}</div>
              <div className="masonry">
                {gitems.map((it) => {
                  const imgUrl = thumbs[it.uuid];
                  return (
                    <div
                      key={it.uuid}
                      className={'tile' + (isVideo(it.mime) ? ' video' : '') + (selected.has(it.uuid) ? ' selected' : '')}
                      onClick={() => onTileClick(it)}
                      onContextMenu={(e) => { e.preventDefault(); inSelectMode ? toggleSelect(it.uuid) : enterSelect(it.uuid); }}
                      onMouseDown={() => onPressStart(it)}
                      onMouseUp={onPressEnd}
                      onMouseLeave={onPressEnd}
                      onTouchStart={() => onPressStart(it)}
                      onTouchEnd={onPressEnd}
                    >
                      {isImage(it.mime) && <img src={'/api/files/' + it.uuid + '/raw'} alt="" loading="lazy" />}
                      {isVideo(it.mime) && <video src={'/api/files/' + it.uuid + '/raw'} muted playsInline />}
                      <div className="overlay" />
                      <div className="name">{it.displayName}</div>
                      {inSelectMode && <div className="sel-check">✓</div>}
                      {!inSelectMode && (
                        <button className="del-btn" onClick={(e) => { e.stopPropagation(); doDelete([it.uuid]); }}>🗑</button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </main>

      <input ref={fileRef} type="file" multiple accept="image/*,video/*,*/*" className="file-input" onChange={onFileSelect} />
      <button className="fab" onClick={() => fileRef.current?.click()} title={t('ops.upload')}>+</button>

      {/* 上传进度 */}
      <UploadPanel lang={lang} active={true} onAllDone={() => load()} />

      {/* 灯箱 */}
      <VaultLightbox
        open={lightbox.open}
        index={lightbox.index}
        items={filtered}
        lang={lang}
        onClose={() => setLightbox((l) => ({ ...l, open: false }))}
        onDelete={(item) => doDelete([item.uuid])}
        onShowDetail={(item) => setDetail(item)}
      />

      {/* 详情 */}
      {detail && <DetailPanel item={detail} lang={lang} onClose={() => setDetail(null)} onDelete={(it) => doDelete([it.uuid])} />}

      {/* 删除确认 */}
      {delConfirm && (
        <ConfirmModal
          lang={lang}
          title={t('confirm.deleteTitle', { n: delConfirm.length })}
          body={t('confirm.deleteBody')}
          items={delConfirm.map((i) => i.displayName)}
          confirmLabel={t('confirm.move')}
          cancelLabel={t('confirm.cancel')}
          onConfirm={() => doDelete(delConfirm.map((i) => i.uuid))}
          onCancel={() => setDelConfirm(null)}
        />
      )}

      {dragging && <div className="drag-overlay">📂 {t('empty.drag')}</div>}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}